import { Cell } from 'api';
import { ISheetAttributeManager } from './ISheetAttributeManager.interface';
import { Logger } from '../../utilities';
import {
  ClearAttributeMappingsPayload,
  DocumentModificationPayload,
  EvaluatedAttributeData,
  Ordinate,
  SheetAttributeData,
  SheetAttributeMapping,
  SheetIndicatorMapping,
  SingleCellIndicator,
} from './models/sheet-attribute-manager.interface';

/**
 * @description The SheetAttributeManager is the core class that is responsible for managing the sheet attribute mapping. Which represents a
 *  dynamic data structure that is used to track the changes to the data leveraging a coordinate system.
 */
class SheetAttributeManager implements ISheetAttributeManager {
  sheetAttributeMapping: SheetAttributeMapping;

  constructor(sheetAttributeMapping: SheetAttributeMapping) {
    this.sheetAttributeMapping = sheetAttributeMapping;
    this.fanOutAllIndicators();
  }

  /**
   * @description Combined method that processes both single cell and grid indicators to avoid data overwriting.
   * This combines the logic from both fanOutSingleCellIndicators and fanOutGridIndicators into one unified process.
   *
   * @returns The sheet attribute manager instance.
   */
  private fanOutAllIndicators() {
    // rebuild sheetAttributeData on each fan out process
    const newData: SheetAttributeData = {};

    this.sheetAttributeMapping.sheetIndicatorMapping.forEach(item => {
      const { singleCellIndicators, gridIndicators, documentReferenceId, sheet } = item;

      // Process single cell indicators
      singleCellIndicators.forEach(singleCellItem => {
        const { xValue, yValue, coordinates, direction } = singleCellItem;

        const hasColumnInitialized = !!newData[yValue];
        if (!hasColumnInitialized) {
          newData[yValue] = {};
        }

        const hasRowInitialized = !!newData[yValue][xValue];
        if (!hasRowInitialized) {
          newData[yValue][xValue] = { referenceData: [] };
        }

        const recordExistsForDocument = newData[yValue][xValue].referenceData.findIndex(
          refItem => refItem.documentReferenceId === documentReferenceId
        );
        if (recordExistsForDocument === -1) {
          newData[yValue][xValue].referenceData.push({
            documentReferenceId,
            sheetMappingConfig: { [sheet]: { mappedCells: [] } },
          });
        }
        const recordForDocumentIndex = newData[yValue][xValue].referenceData.findIndex(
          refItem => refItem.documentReferenceId === documentReferenceId
        );

        // Ensure sheet mapping config exists
        if (!newData[yValue][xValue].referenceData[recordForDocumentIndex].sheetMappingConfig[sheet]) {
          newData[yValue][xValue].referenceData[recordForDocumentIndex].sheetMappingConfig[sheet] = { mappedCells: [] };
        }

        newData[yValue][xValue].referenceData[recordForDocumentIndex].sheetMappingConfig[sheet].mappedCells.push({
          ref: coordinates,
          direction,
        });
      });

      // Process grid indicators
      const { columns, rows } = gridIndicators;

      Object.entries(columns).forEach(([columnLegend, columnOrdinates]) => {
        columnOrdinates.forEach(columnOrdinate => {
          const { value: columnValue } = columnOrdinate;

          // build out mapping in the data
          const hasColumnInitialized = !!newData[columnValue];
          if (!hasColumnInitialized) {
            newData[columnValue] = {};
          }

          Object.entries(rows).forEach(([rowIndex, rowOrdinates]) => {
            rowOrdinates.forEach(rowOrdinate => {
              const { direction: rowDirection, value: rowValue } = rowOrdinate;

              const hasRowInitialized = !!newData[columnValue][rowValue];
              if (!hasRowInitialized) {
                newData[columnValue][rowValue] = { referenceData: [] };
              }

              // optimize the finds here
              const recordExistsForDocument = newData[columnValue][rowValue].referenceData.findIndex(
                refItem => refItem.documentReferenceId === documentReferenceId && refItem.sheetMappingConfig[sheet]
              );
              if (recordExistsForDocument === -1) {
                newData[columnValue][rowValue].referenceData.push({
                  documentReferenceId,
                  sheetMappingConfig: { [sheet]: { mappedCells: [] } },
                });
              }
              const recordForDocumentIndex = newData[columnValue][rowValue].referenceData.findIndex(
                refItem => refItem.documentReferenceId === documentReferenceId && refItem.sheetMappingConfig[sheet]
              );

              const legend = `${columnLegend.toUpperCase()}${rowIndex}`;
              const hasCell = newData[columnValue][rowValue].referenceData[recordForDocumentIndex].sheetMappingConfig[
                sheet
              ].mappedCells.find(cellItem => cellItem.ref === legend && cellItem.direction === rowDirection);
              if (!hasCell) {
                newData[columnValue][rowValue].referenceData[recordForDocumentIndex].sheetMappingConfig[
                  sheet
                ].mappedCells.push({
                  ref: legend,
                  direction: rowDirection,
                });
              }
            });
          });
        });
      });
    });

    this.sheetAttributeMapping.data = newData;
    return this;
  }

  /**
   * @description Fan out is the terminology used to describe the process of propagating changes to the sheet attribute mapping,
   *  this is done by iterating over the sheetIndicatorMapping and essentially taking the dot product of the columns and rows
   *  to create the new data structure. Now uses a combined approach to handle both single cell and grid indicators.
   *
   * @returns The sheet attribute manager instance.
   */
  private fanOutIndicators(): this {
    return this.fanOutAllIndicators();
  }

  /**
   * @description The handleSingularAddition is the method that is responsible for adding a singular single cell indicator to the sheet attribute mapping.
   *  There exists validation to ensure that the single cell indicator is not a duplicate by checking coordinates, xValue, yValue, and direction.
   *
   * @param documentModificationPayload The document modification payload is the document id and sheet name that the change is being made to.
   * @param singleCellIndicator The single cell indicator is the single cell indicator that is being added to the sheet attribute mapping.
   *
   * @returns The sheet attribute manager instance.
   */
  handleSingularAddition(
    documentModificationPayload: DocumentModificationPayload,
    singleCellIndicator: SingleCellIndicator
  ): ISheetAttributeManager {
    const { documentReferenceId, sheet } = documentModificationPayload;
    // find the sheetIndicatorMapping for the sheet and document
    const indicatorsForSheetAndDocumentIndex = this.sheetAttributeMapping.sheetIndicatorMapping.findIndex(
      item => item.documentReferenceId === documentReferenceId && item.sheet === sheet
    );

    if (indicatorsForSheetAndDocumentIndex === -1) {
      this.sheetAttributeMapping.sheetIndicatorMapping.push({
        sheet,
        documentReferenceId,
        gridIndicators: {
          columns: {},
          rows: {},
        },
        singleCellIndicators: [],
      });
    }

    const indicatorsForSheetAndDocument = this.sheetAttributeMapping.sheetIndicatorMapping.find(
      item => item.documentReferenceId === documentReferenceId && item.sheet === sheet
    )!;

    // validate this is not a duplicate indicator
    const existingSingleCellIndicator = indicatorsForSheetAndDocument.singleCellIndicators.find(
      item => item.coordinates === singleCellIndicator.coordinates
    );
    if (existingSingleCellIndicator) {
      Logger.debug(
        `Single cell indicator already exists for: ${singleCellIndicator.coordinates}(x: ${singleCellIndicator.xValue}, y ${singleCellIndicator.yValue}) for Document(${documentReferenceId}${sheet})`
      );
      return this;
    }

    indicatorsForSheetAndDocument.singleCellIndicators.push(singleCellIndicator);

    const newIndicatorsForSheetAndDocumentIndex = this.sheetAttributeMapping.sheetIndicatorMapping.findIndex(
      item => item.documentReferenceId === documentReferenceId && item.sheet === sheet
    );

    this.sheetAttributeMapping.sheetIndicatorMapping[newIndicatorsForSheetAndDocumentIndex] =
      indicatorsForSheetAndDocument;

    return this.fanOutIndicators();
  }

  /**
   * @description removes a single cell indicator by x and y value which are the values from the source sheet
   * @param CellIndicators single cell indicator metrics 
   * @returns The attribute manager instance
   */
  removeSingleCellByXYValue(
    { xValue, yValue }: Pick<SingleCellIndicator, 'xValue' | 'yValue'>
  ) {
    this.sheetAttributeMapping.sheetIndicatorMapping.forEach((item) => {
      // Keep indicators where EITHER xValue doesn't match OR yValue doesn't match
      // Remove only when BOTH xValue AND yValue match
      const filteredSingleCells = item.singleCellIndicators.filter((siIndicator) =>
        siIndicator.xValue !== xValue || siIndicator.yValue !== yValue
      )

      // eslint-disable-next-line no-param-reassign
      item.singleCellIndicators = filteredSingleCells;
    })

    return this.fanOutIndicators();
  }

  /**
   * @description Handles inserting a new column into the displayed indicators of the configuration.
   *  There exists validation to ensure that the ordinate is not a duplicate by checking the value and direction on the column
   *
   * @param documentModificationPayload The document modification payload is the document id and sheet name that the change is being made to.
   * @param columnLetter The column letter that is being added.
   * @param ordinate The ordinate is the value and direction that is being added to the column.
   *
   * @returns The sheet attribute manager instance.
   */
  handleColumnAddition(
    documentModificationPayload: DocumentModificationPayload,
    columnLetter: string,
    ordinate: Ordinate
  ): ISheetAttributeManager {
    const { documentReferenceId, sheet } = documentModificationPayload;
    // push column letter into displayed indicators for the sheet and document
    const indicatorsForSheetAndDocumentIndex = this.sheetAttributeMapping.sheetIndicatorMapping.findIndex(
      indicMapping => indicMapping.sheet === sheet && indicMapping.documentReferenceId === documentReferenceId
    );

    const indicatorsForSheetAndDocument: SheetIndicatorMapping =
      indicatorsForSheetAndDocumentIndex === -1
        ? {
          sheet,
          documentReferenceId,
          gridIndicators: {
            columns: {},
            rows: {},
          },
          singleCellIndicators: [],
        }
        : this.sheetAttributeMapping.sheetIndicatorMapping[indicatorsForSheetAndDocumentIndex];

    const hasColumnInitialized = indicatorsForSheetAndDocument.gridIndicators.columns[columnLetter];

    if (hasColumnInitialized) {
      // ensure that the ordinate is not a duplicate by checking the value and direction
      const existingOrdinate = indicatorsForSheetAndDocument.gridIndicators.columns[columnLetter].find(
        item => item.value === ordinate.value && item.direction === ordinate.direction
      );
      if (existingOrdinate) {
        Logger.debug(
          `Ordinate already exists for: ${columnLetter}(${ordinate.value}, ${ordinate.direction}) for Document(${documentReferenceId}${sheet})`
        );
        return this;
      }

      indicatorsForSheetAndDocument.gridIndicators.columns[columnLetter].push(ordinate);
    } else {
      indicatorsForSheetAndDocument.gridIndicators.columns[columnLetter] = [ordinate];
    }

    if (indicatorsForSheetAndDocumentIndex === -1) {
      this.sheetAttributeMapping.sheetIndicatorMapping.push(indicatorsForSheetAndDocument);
    } else {
      this.sheetAttributeMapping.sheetIndicatorMapping[indicatorsForSheetAndDocumentIndex] =
        indicatorsForSheetAndDocument;
    }

    return this.fanOutIndicators();
  }

  /**
   * @description Handles inserting a new row into the displayed indicators of the configuration.
   *  There exists validation to ensure that the ordinate is not a duplicate by checking the value and direction on the row
   *
   * @param documentModificationPayload The document modification payload is the document id and sheet name that the change is being made to.
   * @param rowIndex The row index that is being added.
   * @param ordinate The ordinate is the value and direction that is being added to the row.
   *
   * @returns The sheet attribute manager instance.
   */
  handleRowAddition(
    documentModificationPayload: DocumentModificationPayload,
    rowIndex: number,
    ordinate: Ordinate
  ): this {
    const { documentReferenceId, sheet } = documentModificationPayload;
    // push column letter into displayed indicators for the sheet and document
    const indicatorsForSheetAndDocumentIndex = this.sheetAttributeMapping.sheetIndicatorMapping.findIndex(
      indicMapping => indicMapping.sheet === sheet && indicMapping.documentReferenceId === documentReferenceId
    );

    let indicatorsForSheetAndDocument =
      this.sheetAttributeMapping.sheetIndicatorMapping[indicatorsForSheetAndDocumentIndex];

    if (indicatorsForSheetAndDocumentIndex === -1) {
      indicatorsForSheetAndDocument = {
        sheet,
        documentReferenceId,
        gridIndicators: {
          columns: {},
          rows: {},
        },
        singleCellIndicators: [],
      };
    }

    const hasRowInitialized = indicatorsForSheetAndDocument.gridIndicators.rows[rowIndex];

    if (hasRowInitialized) {
      const existingOrdinate = indicatorsForSheetAndDocument.gridIndicators.rows[rowIndex].find(
        item => item.value === ordinate.value && item.direction === ordinate.direction
      );
      if (existingOrdinate) {
        Logger.debug(
          `Ordinate already exists for: ${rowIndex}(${ordinate.value}, ${ordinate.direction}) for Document(${documentReferenceId}${sheet})`
        );
        return this;
      }

      indicatorsForSheetAndDocument.gridIndicators.rows[rowIndex].push(ordinate);
    } else {
      indicatorsForSheetAndDocument.gridIndicators.rows[rowIndex] = [ordinate];
    }

    if (indicatorsForSheetAndDocumentIndex === -1) {
      this.sheetAttributeMapping.sheetIndicatorMapping.push(indicatorsForSheetAndDocument);
    } else {
      this.sheetAttributeMapping.sheetIndicatorMapping[indicatorsForSheetAndDocumentIndex] =
        indicatorsForSheetAndDocument;
    }

    return this.fanOutIndicators();
  }

  /**
   * @description Removes a column ordinate from the sheet attribute mapping.
   *
   * @param documentModificationPayload The document modification payload is the document id and sheet name that the change is being made to.
   * @param columnLegend The column legend that is being removed.
   * @param ordinate The ordinate that is being removed.
   *
   * @returns The sheet attribute manager instance.
   */
  removeColumnOrdinate(
    documentModificationPayload: DocumentModificationPayload,
    columnLegend: string,
    ordinate: Ordinate
  ) {
    const indicatorForSheet = this.sheetAttributeMapping.sheetIndicatorMapping.findIndex(
      item =>
        item.documentReferenceId === documentModificationPayload.documentReferenceId &&
        item.sheet === documentModificationPayload.sheet
    );
    if (indicatorForSheet === -1) {
      Logger.debug(
        `Indicator for Document(${documentModificationPayload.documentReferenceId})sheet${documentModificationPayload.sheet}`
      );
      return this;
    }

    const gridIndicatorsForColumn =
      this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheet].gridIndicators.columns[columnLegend];

    const indexOfOrdinate = gridIndicatorsForColumn.findIndex(
      item => item.value === ordinate.value && item.direction === ordinate.direction
    );
    if (indexOfOrdinate === -1) {
      Logger.debug(
        `Ordinate not found for: ${columnLegend}(${ordinate.value}, ${ordinate.direction}) for Document(${documentModificationPayload.documentReferenceId}${documentModificationPayload.sheet})`
      );
      return this;
    }

    this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheet].gridIndicators.columns[columnLegend].splice(
      indexOfOrdinate,
      1
    );

    const isEmpty =
      Object.keys(
        this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheet].gridIndicators.columns[columnLegend]
      ).length === 0;
    if (isEmpty) {
      delete this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheet].gridIndicators.columns[columnLegend];
    }

    return this.fanOutIndicators();
  }

  /**
   * @description Removes a row ordinate from the sheet attribute mapping.
   *
   * @param documentModificationPayload The document modification payload is the document id and sheet name that the change is being made to.
   * @param rowNumber The row number that is being removed.
   * @param ordinate The ordinate that is being removed.
   *
   * @returns The sheet attribute manager instance.
   */
  removeRowOrdinate(documentModificationPayload: DocumentModificationPayload, rowNumber: number, ordinate: Ordinate) {
    const indicatorForSheet = this.sheetAttributeMapping.sheetIndicatorMapping.findIndex(
      item =>
        item.documentReferenceId === documentModificationPayload.documentReferenceId &&
        item.sheet === documentModificationPayload.sheet
    );
    if (indicatorForSheet === -1) {
      Logger.debug(
        `Indicator for Document(${documentModificationPayload.documentReferenceId})sheet${documentModificationPayload.sheet}`
      );
      return this;
    }

    const gridIndicatorsForRow =
      this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheet].gridIndicators.rows[rowNumber];

    const indexOfOrdinate = gridIndicatorsForRow.findIndex(
      item => item.value === ordinate.value && item.direction === ordinate.direction
    );
    if (indexOfOrdinate === -1) {
      Logger.debug(
        `Ordinate not found for: ${rowNumber}(${ordinate.value}, ${ordinate.direction}) for Document(${documentModificationPayload.documentReferenceId}${documentModificationPayload.sheet})`
      );
      return this;
    }

    this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheet].gridIndicators.rows[rowNumber].splice(
      indexOfOrdinate,
      1
    );

    const isEmpty =
      Object.keys(this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheet].gridIndicators.rows[rowNumber])
        .length === 0;
    if (isEmpty) {
      delete this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheet].gridIndicators.rows[rowNumber];
    }

    return this.fanOutIndicators();
  }

  /**
   * @description Accepts an array of ordinates and will perform set operations to ensure that the sheet attribute mapping is up to date.
   * @param documentModificationPayload The document modification payload is the document id and sheet name that the change is being made to.
   * @param columnLegend The column legend that is being added.
   * @param columnValues The column values that are being added.
   *
   * @returns The sheet attribute manager instance.
   */
  handleColumnUpdate(
    documentModificationPayload: DocumentModificationPayload,
    columnLegend: string,
    columnValues: Ordinate[]
  ): ISheetAttributeManager {
    const indicatorForSheet = this.sheetAttributeMapping.sheetIndicatorMapping.find(
      item =>
        item.documentReferenceId === documentModificationPayload.documentReferenceId &&
        item.sheet === documentModificationPayload.sheet
    );
    if (!indicatorForSheet && columnValues.length > 0) {
      Logger.debug(
        `Indicator for Document(${documentModificationPayload.documentReferenceId})sheet${documentModificationPayload.sheet}`
      );
      this.sheetAttributeMapping.sheetIndicatorMapping.push({
        sheet: documentModificationPayload.sheet,
        documentReferenceId: documentModificationPayload.documentReferenceId,
        singleCellIndicators: [],
        gridIndicators: {
          columns: {
            [columnLegend]: columnValues,
          },
          rows: {},
        },
      });

      return this.fanOutIndicators();
    }

    const gridIndicatorsForColumn = indicatorForSheet!.gridIndicators.columns[columnLegend];

    if (!gridIndicatorsForColumn) {
      columnValues.forEach(columnValue => {
        this.handleColumnAddition(documentModificationPayload, columnLegend, columnValue);
      });

      return this.fanOutIndicators();
    }

    // anything that is in current not in colValues -> remove
    [...gridIndicatorsForColumn].forEach(currentGridIndicator => {
      if (!columnValues.length) {
        this.removeColumnOrdinate(documentModificationPayload, columnLegend, currentGridIndicator);
      } else {
        const existsInIncomingSetAndIsSaved = columnValues.find(
          item => item.value === currentGridIndicator.value && item.direction === currentGridIndicator.direction
        );

        if (!existsInIncomingSetAndIsSaved) {
          this.removeColumnOrdinate(documentModificationPayload, columnLegend, currentGridIndicator);
        }
      }
    });

    // anything that is in colValues not in currentColumnValues -> add
    columnValues.forEach(incomingColumnValue => {
      const existsInCurrentSetAndIsSaved = gridIndicatorsForColumn.find(
        item => item.value === incomingColumnValue.value && item.direction === incomingColumnValue.direction
      );

      if (!existsInCurrentSetAndIsSaved) {
        this.handleColumnAddition(documentModificationPayload, columnLegend, incomingColumnValue);
      }
    });

    return this.fanOutIndicators();
  }


  /**
   * @description Updates the order of magnitude for a given document and sheet.
   * @param documentModificationPayload The document modification payload is the document id and sheet name that the change is being made to.
   * @param orderOfMagnitude The order of magnitude that is being added.
   * @returns The sheet attribute manager instance.
   */
  handleOrderOfMagnitudeUpdate(
    documentModificationPayload: DocumentModificationPayload,
    orderOfMagnitude: number
  ): ISheetAttributeManager {
    const indicatorForSheetIndex = this.sheetAttributeMapping.sheetIndicatorMapping.findIndex(
      item => item.documentReferenceId === documentModificationPayload.documentReferenceId && item.sheet === documentModificationPayload.sheet
    );

    if (indicatorForSheetIndex === -1) {
      Logger.debug(
        `Creating new indicator for Document(${documentModificationPayload.documentReferenceId})sheet${documentModificationPayload.sheet}`
      );
      this.sheetAttributeMapping.sheetIndicatorMapping.push({
        sheet: documentModificationPayload.sheet,
        documentReferenceId: documentModificationPayload.documentReferenceId,
        singleCellIndicators: [],
        gridIndicators: {
          columns: {},
          rows: {},
        },
        orderOfMagnitude,
      });

      return this.fanOutIndicators();
    }

    this.sheetAttributeMapping.sheetIndicatorMapping[indicatorForSheetIndex].orderOfMagnitude = orderOfMagnitude;
    return this.fanOutIndicators();
  }

  /**
   *
   * @description Accepts an array of ordinates and will perform set operations to ensure that the sheet attribute mapping is up to date.
   * @param documentModificationPayload The document modification payload is the document id and sheet name that the change is being made to.
   * @param rowNumber The row number that is being added.
   * @param ordinates The row values that are being added.
   *
   * @returns The sheet attribute manager instance.
   */
  handleRowUpdate(
    documentModificationPayload: DocumentModificationPayload,
    rowNumber: number,
    ordinates: Ordinate[]
  ): ISheetAttributeManager {
    const indicatorForSheet = this.sheetAttributeMapping.sheetIndicatorMapping.find(
      item =>
        item.documentReferenceId === documentModificationPayload.documentReferenceId &&
        item.sheet === documentModificationPayload.sheet
    );
    if (!indicatorForSheet && ordinates.length > 0) {
      Logger.debug(
        `Indicator for Document(${documentModificationPayload.documentReferenceId})sheet${documentModificationPayload.sheet}`
      );
      this.sheetAttributeMapping.sheetIndicatorMapping.push({
        sheet: documentModificationPayload.sheet,
        documentReferenceId: documentModificationPayload.documentReferenceId,
        singleCellIndicators: [],
        gridIndicators: {
          columns: {},
          rows: {
            [rowNumber]: ordinates,
          },
        },
      });

      return this.fanOutIndicators();
    }

    const gridIndicatorsForRow = indicatorForSheet!.gridIndicators.rows[rowNumber];

    if (!gridIndicatorsForRow) {
      ordinates.forEach(rowValue => {
        this.handleRowAddition(documentModificationPayload, rowNumber, rowValue);
      });

      return this.fanOutIndicators();
    }
    // anything that is in current not in colValues -> remove
    [...gridIndicatorsForRow].forEach(currentGridIndicator => {
      if (!ordinates.length) {
        this.removeRowOrdinate(documentModificationPayload, rowNumber, currentGridIndicator);
      } else {
        const existsInIncomingSetAndIsSaved = ordinates.find(
          item => item.value === currentGridIndicator.value && item.direction === currentGridIndicator.direction
        );

        if (!existsInIncomingSetAndIsSaved) {
          this.removeRowOrdinate(documentModificationPayload, rowNumber, currentGridIndicator);
        }
      }
    });

    // anything that is in rowValues not in currentRowValues -> add
    ordinates.forEach(incomingRowValue => {
      const existsInCurrentSetAndIsSaved = gridIndicatorsForRow.find(
        item => item.value === incomingRowValue.value && item.direction === incomingRowValue.direction
      );

      if (!existsInCurrentSetAndIsSaved) {
        this.handleRowAddition(documentModificationPayload, rowNumber, incomingRowValue);
      }
    });

    return this.fanOutIndicators();
  }

  /**
   * @description Clears the attribute mappings for a given document reference id and sheet name.
   * if no sheet name is provided, all records for the document will be cleared.
   * if a sheet name is provided, only the records for that sheet will be cleared.
   * @param docRefPayload The document reference id and sheet name that is being cleared.
   * @returns The sheet attribute manager instance.
   */
  handleClearMappings(docRefPayload: ClearAttributeMappingsPayload) {
    const { documentReferenceId, sheet } = docRefPayload;

    // Filter out items that match the criteria
    this.sheetAttributeMapping.sheetIndicatorMapping = this.sheetAttributeMapping.sheetIndicatorMapping.filter(item => {
      // If no sheet is provided, remove all records for this document
      if (!sheet && item.documentReferenceId === documentReferenceId) {
        return false;
      }

      // If sheet is provided, only remove records for that specific sheet
      if (sheet && item.documentReferenceId === documentReferenceId && item.sheet === sheet) {
        return false;
      }

      return true;
    });

    const response = this.fanOutIndicators();
    return response;
  }

  /**
   *
   * @description Extracts a SINGLE number out of a string
   * @param text {string} Numeric string
   * @returns number if a number is extracted from the string, or null if no number is found in the string
   */
  extractNumberFromString(text: string): number | null {
    // Support scientific notation (e.g., 1.5e10, 2E-5) and regular numbers
    const NUMBER_EXTRACTION_REGEX =
      /(?<![\d\w])(?:\((\d+(?:\.\d+)?[eE][+-]?\d+|\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?|\.\d+)\)|(-?\d+(?:\.\d+)?[eE][+-]?\d+|-?\d{1,3}(?:,\d{3})*(?:\.\d+)?|-?\d+(?:\.\d+)?|-?\.\d+))(?![\d\w])/;
    const match = text.match(NUMBER_EXTRACTION_REGEX);
    if (!match) return null;

    const value = match[1] || match[2]; // parenVal or normalVal
    const num = parseFloat(value.replace(/,/g, '')); // remove commas and convert to number

    // Handle -0 edge case (convert to positive 0)
    if (Object.is(num, -0)) return 0;

    return match[1] ? -num : num; // if it was in parentheses, make it negative
  }

  /**
   *
   * @description This function evaluates the mappedCells for each sheetMappingConfig into a value based on what is provided in the  cell mapping
   * @param cellMappingBySheet A list of cells by sheet from a workbook {balanceSheet: {a1: Cell, a2: cell, a3: cell}}
   * @returns The evaluated attribute data {2020: {total_revenue: {value: 100}}}
   */
  calculateValues(cellMappingBySheet: Record<string, Record<string, Cell>>): EvaluatedAttributeData {
    // iterate over the data for each column and row and document and sum up the values
    const evaluatedData: EvaluatedAttributeData = {};

    Object.keys(this.sheetAttributeMapping.data).forEach(columnarMetric => {
      Object.keys(this.sheetAttributeMapping.data[columnarMetric]).forEach(rowMetric => {
        const { referenceData } = this.sheetAttributeMapping.data[columnarMetric][rowMetric];

        referenceData.forEach(item => {
          const sheetNames = Object.keys(item.sheetMappingConfig);
          sheetNames.forEach(sheetName => {
            // Verify that this sheet belongs to the correct document by checking sheetIndicatorMapping
            // This ensures we don't use cells from the wrong document when multiple documents have the same sheet name
            const sheetMapping = this.sheetAttributeMapping.sheetIndicatorMapping.find(
              mapping => mapping.sheet === sheetName && mapping.documentReferenceId === item.documentReferenceId
            );

            // Skip if we can't find a matching sheet mapping for this document
            if (!sheetMapping) {
              return;
            }

            // Use composite key (documentId_sheetName) to get cells from the correct document
            // Fall back to sheet name only for backward compatibility
            const compositeKey = `${item.documentReferenceId}_${sheetName}`;
            const cellSetForSheet = cellMappingBySheet[compositeKey] || cellMappingBySheet[sheetName];

            const mappingForSheet = item.sheetMappingConfig[sheetName].mappedCells;

            const calculatedValue = mappingForSheet.reduce((acc, curr) => {
              const cellForSheetRefValue = cellSetForSheet?.[curr.ref]?.value;
              const valueForRef = cellForSheetRefValue
                ? { value: this.extractNumberFromString(cellForSheetRefValue) }
                : { value: 0 };

              let parsedValue = valueForRef.value ? valueForRef.value : 0;

              if (curr.direction === 'negative') {
                parsedValue = -parsedValue;
              }

              const orderOfMagnitude = sheetMapping.orderOfMagnitude ?? 1;

              return acc + (Number.isNaN(parsedValue) ? 0 : parsedValue * orderOfMagnitude);
            }, 0);

            // Initialize the columnarMetric object if it doesn't exist
            if (!evaluatedData[columnarMetric]) {
              evaluatedData[columnarMetric] = {};
            }

            // Initialize or accumulate the value for this metric
            const nonNegatedValue = rowMetric.startsWith('neg-') ? rowMetric.replace('neg-', '') : rowMetric;
            if (!evaluatedData[columnarMetric][nonNegatedValue]) {
              evaluatedData[columnarMetric][nonNegatedValue] = { value: 0 };
            }
            evaluatedData[columnarMetric][nonNegatedValue].value += calculatedValue;
          });
        });
      });
    });

    return evaluatedData;
  }

  /**
   * Check if there are any unaccepted AI-generated ordinates in the mapping.
   * @returns true if there are unaccepted AI mappings, false otherwise
   */
  hasUnacceptedAIMappings(): boolean {
    return this.sheetAttributeMapping.sheetIndicatorMapping.some((sheetMapping) => {
      const { gridIndicators } = sheetMapping;

      // Check columns
      const hasUnacceptedColumns = Object.values(gridIndicators.columns).some((columnOrdinates) =>
        columnOrdinates.some((ordinate) => ordinate.isAiGenerated && !ordinate.isAccepted)
      );

      if (hasUnacceptedColumns) {
        return true;
      }

      // Check rows
      return Object.values(gridIndicators.rows).some((rowOrdinates) =>
        rowOrdinates.some((ordinate) => ordinate.isAiGenerated && !ordinate.isAccepted)
      );
    });
  }

  /**
   * Accept a specific ordinate by marking it as accepted.
   * @param columnOrRow - 'column' or 'row'
   * @param identifier - The column letter or row number
   * @param value - The ordinate value
   */
  acceptOrdinate(columnOrRow: 'column' | 'row', identifier: string, value: string): boolean {
    let found = false;
    this.sheetAttributeMapping.sheetIndicatorMapping.forEach((sheetMapping) => {
      const { gridIndicators } = sheetMapping;

      const ordinatesKey = columnOrRow === 'column' ? 'columns' : 'rows';
      const ordinatesMap = gridIndicators[ordinatesKey] as { [key: string]: Ordinate[] };
      const ordinates = ordinatesMap[identifier];

      if (ordinates) {
        ordinates.forEach((ordinate) => {
          if (ordinate.value === value && ordinate.isAiGenerated) {
            // eslint-disable-next-line no-param-reassign
            ordinate.isAccepted = true;
            found = true;
          }
        });
      }
    });

    this.fanOutIndicators();

    return found;
  }

  /**
   * Accept a specific ordinate by value, optionally limiting to a specific column or row.
   * @param value - The ordinate value to accept
   * @param columnOrRowKey - Optional column letter or row number to limit the search
   * @param type - Whether to search in 'column' or 'row' ordinates
   */
  acceptOrdinateByValue(value: string, documentReferenceId: number, columnOrRowKey: string | number, type?: 'column' | 'row'): boolean {
    let found = false;
    this.sheetAttributeMapping.sheetIndicatorMapping.forEach((sheetMapping) => {
      const { gridIndicators, documentReferenceId: mappingDocumentReferenceId } = sheetMapping;
      if (mappingDocumentReferenceId !== documentReferenceId) {
        return;
      }

      // If columnOrRowKey is specified, only update in that specific column/row
      if (columnOrRowKey && type === 'column') {
        const columnOrdinates = gridIndicators.columns[columnOrRowKey];
        if (columnOrdinates) {
          columnOrdinates.forEach((ordinate) => {
            if (ordinate.value === value && ordinate.isAiGenerated) {
              // eslint-disable-next-line no-param-reassign
              ordinate.isAccepted = true;
              found = true;
            }
          });
        }
        return; // Don't search other columns/rows
      }

      if (columnOrRowKey && type === 'row') {
        // Convert string row key to number for lookup
        const rowKey = parseInt(String(columnOrRowKey), 10);
        const rowOrdinates = gridIndicators.rows[rowKey];
        if (rowOrdinates) {
          rowOrdinates.forEach((ordinate) => {
            if (ordinate.value === value && ordinate.isAiGenerated) {
              // eslint-disable-next-line no-param-reassign
              ordinate.isAccepted = true;
              found = true;
            }
          });
        }
        return; // Don't search other columns/rows
      }

      // Fallback: Search in all columns and rows if no specific location provided
      Object.values(gridIndicators.columns).forEach((columnOrdinates) => {
        columnOrdinates.forEach((ordinate) => {
          if (ordinate.value === value && ordinate.isAiGenerated) {
            // eslint-disable-next-line no-param-reassign
            ordinate.isAccepted = true;
            found = true;
          }
        });
      });

      Object.values(gridIndicators.rows).forEach((rowOrdinates) => {
        rowOrdinates.forEach((ordinate) => {
          if (ordinate.value === value && ordinate.isAiGenerated) {
            // eslint-disable-next-line no-param-reassign
            ordinate.isAccepted = true;
            found = true;
          }
        });
      });
    });

    this.fanOutAllIndicators();

    return found;
  }

  /**
   * Decline (remove) a specific ordinate by value, optionally limiting to a specific column or row.
   * This removes unaccepted AI-generated ordinates.
   * @param value - The ordinate value to decline/remove
   * @param columnOrRowKey - Optional column letter or row number to limit the search
   * @param type - Whether to search in 'column' or 'row' ordinates
   */
  declineOrdinateByValue(value: string, documentReferenceId: number, columnOrRowKey?: string | number, type?: 'column' | 'row'): boolean {
    let found = false;
    this.sheetAttributeMapping.sheetIndicatorMapping.forEach((sheetMapping) => {
      const { gridIndicators, documentReferenceId: mappingDocumentReferenceId } = sheetMapping;
      if (mappingDocumentReferenceId !== documentReferenceId) {
        return;
      }


      // If columnOrRowKey is specified, only filter in that specific column/row
      if (columnOrRowKey && type === 'column') {
        const columnOrdinates = gridIndicators.columns[columnOrRowKey];
        if (columnOrdinates) {
          const originalLength = columnOrdinates.length;
          const filtered = columnOrdinates.filter(
            (ordinate) => !(ordinate.value === value && ordinate.isAiGenerated && !ordinate.isAccepted)
          );
          if (filtered.length < originalLength) {
            // eslint-disable-next-line no-param-reassign
            gridIndicators.columns[columnOrRowKey] = filtered;
            found = true;
          }
        }
        return; // Don't search other columns/rows
      }

      if (columnOrRowKey && type === 'row') {
        // Convert string row key to number for lookup
        const rowKey = parseInt(String(columnOrRowKey) as string, 10);
        const rowOrdinates = gridIndicators.rows[rowKey];
        if (rowOrdinates) {
          const originalLength = rowOrdinates.length;
          const filtered = rowOrdinates.filter(
            (ordinate) => !(ordinate.value === value && ordinate.isAiGenerated && !ordinate.isAccepted)
          );
          if (filtered.length < originalLength) {
            // eslint-disable-next-line no-param-reassign
            gridIndicators.rows[rowKey] = filtered;
            found = true;
          }
        }
        return; // Don't search other columns/rows
      }

      // Fallback: Search and filter all columns and rows if no specific location provided
      Object.entries(gridIndicators.columns).forEach(([columnKey, columnOrdinates]) => {
        const originalLength = columnOrdinates.length;
        const filtered = columnOrdinates.filter(
          (ordinate) => !(ordinate.value === value && ordinate.isAiGenerated && !ordinate.isAccepted)
        );
        if (filtered.length < originalLength) {
          // eslint-disable-next-line no-param-reassign
          gridIndicators.columns[columnKey] = filtered;
          found = true;
        }
      });

      Object.entries(gridIndicators.rows).forEach(([rowKey, rowOrdinates]) => {
        const originalLength = rowOrdinates.length;
        const filtered = rowOrdinates.filter(
          (ordinate) => !(ordinate.value === value && ordinate.isAiGenerated && !ordinate.isAccepted)
        );
        if (filtered.length < originalLength) {
          // eslint-disable-next-line no-param-reassign
          gridIndicators.rows[rowKey as unknown as number] = filtered;
          found = true;
        }
      });
    });

    this.fanOutAllIndicators();

    return found;
  }

  /**
   * Accept all AI-generated ordinates in the mapping.
   */
  acceptAllAIMappings(): void {
    this.sheetAttributeMapping.sheetIndicatorMapping.forEach((sheetMapping) => {
      const { gridIndicators } = sheetMapping;

      // Accept all columns
      Object.values(gridIndicators.columns).forEach((columnOrdinates) => {
        columnOrdinates.forEach((ordinate) => {
          if (ordinate.isAiGenerated) {
            // eslint-disable-next-line no-param-reassign
            ordinate.isAccepted = true;
          }
        });
      });

      // Accept all rows
      Object.values(gridIndicators.rows).forEach((rowOrdinates) => {
        rowOrdinates.forEach((ordinate) => {
          if (ordinate.isAiGenerated) {
            // eslint-disable-next-line no-param-reassign
            ordinate.isAccepted = true;
          }
        });
      });
    });

    this.fanOutAllIndicators();
  }

  /**
   * Get ordinate metadata for a specific value.
   * Used to populate options with AI metadata.
   * @param value - The ordinate value (e.g., row alias)
   * @returns Ordinate metadata or undefined
   */
  getOrdinateMetadata(
    value: string,
    columnOrRowKey?: string,
    type?: 'column' | 'row',
    sheet?: string,
    documentReferenceId?: number
  ): { isAiGenerated?: boolean; isAccepted?: boolean; commentary?: string[]; confidence?: number } | undefined {
    let result: { isAiGenerated?: boolean; isAccepted?: boolean; commentary?: string[]; confidence?: number } | undefined;

    this.sheetAttributeMapping.sheetIndicatorMapping.some((sheetMapping) => {
      // Only consider the indicator mapping that belongs to the current sheet+document
      if (sheet && sheetMapping.sheet !== sheet) return false;
      if (documentReferenceId != null && sheetMapping.documentReferenceId !== documentReferenceId) return false;

      const { gridIndicators } = sheetMapping;

      // If columnOrRowKey is specified, only check that specific column/row
      if (columnOrRowKey && type === 'column') {
        const columnOrdinates = gridIndicators.columns[columnOrRowKey];
        if (columnOrdinates) {
          const foundOrdinate = columnOrdinates.find((ordinate) => ordinate.value === value);
          if (foundOrdinate) {
            result = {
              isAiGenerated: foundOrdinate.isAiGenerated,
              isAccepted: foundOrdinate.isAccepted,
              commentary: foundOrdinate.commentary,
              confidence: foundOrdinate.confidence,
            };
            return true;
          }
        }
        return false;
      }

      if (columnOrRowKey && type === 'row') {
        const rowOrdinates = gridIndicators.rows[columnOrRowKey as unknown as number];
        if (rowOrdinates) {
          const foundOrdinate = rowOrdinates.find((ordinate: Ordinate) => ordinate.value === value);
          if (foundOrdinate) {
            result = {
              isAiGenerated: foundOrdinate.isAiGenerated,
              isAccepted: foundOrdinate.isAccepted,
              commentary: foundOrdinate.commentary,
              confidence: foundOrdinate.confidence,
            };
            return true;
          }
        }
        return false;
      }

      // Fallback to global search if no columnOrRowKey specified
      // Check columns
      const columnOrdinatesArray = Object.values(gridIndicators.columns);
      const columnResult = columnOrdinatesArray.some((columnOrdinates) => {
        const foundOrdinate = columnOrdinates.find((ordinate) => ordinate.value === value);
        if (foundOrdinate) {
          result = {
            isAiGenerated: foundOrdinate.isAiGenerated,
            isAccepted: foundOrdinate.isAccepted,
            commentary: foundOrdinate.commentary,
            confidence: foundOrdinate.confidence,
          };
          return true;
        }
        return false;
      });

      if (columnResult) {
        return true;
      }

      // Check rows
      const rowOrdinatesArray = Object.values(gridIndicators.rows);
      return rowOrdinatesArray.some((rowOrdinates) => {
        const foundOrdinate = rowOrdinates.find((ordinate) => ordinate.value === value);
        if (foundOrdinate) {
          result = {
            isAiGenerated: foundOrdinate.isAiGenerated,
            isAccepted: foundOrdinate.isAccepted,
            commentary: foundOrdinate.commentary,
            confidence: foundOrdinate.confidence,
          };
          return true;
        }
        return false;
      });
    });

    this.fanOutIndicators()

    return result;
  }
}

export default SheetAttributeManager;
